import type { ScheduledTestRecord } from '../../services/scheduledTestService';
import type { SurveillancePlanRecord } from '../../types/surveillance';
import { coerceDate, formatDateLabel } from '../dateLabels';
import { type BookingSummary, type SourceStatus, isUsable } from './types';

/**
 * "Coming up" — the dated things ahead of the member, in order.
 *
 * Everything here must have a real date. Plan items whose `nextDue` is free
 * text ("annual", "every 6 months") are EXCLUDED rather than shown as "date
 * pending": a chronological list is sorted by date, so an entry with no date
 * has nowhere to sit and reads as noise. The plan page is where undated
 * cadence belongs.
 *
 * The same principle rules out deriving a blood-test date from plan cadence.
 * Only a `scheduled_tests` document represents a date the practice has actually
 * committed to.
 */

export type ComingUpKind = 'test' | 'interview' | 'plan_item';

export interface ComingUpEvent {
  id: string;
  at: Date;
  dateLabel: string;
  title: string;
  detail: string;
  href?: string;
  kind: ComingUpKind;
  /** True while the appointment is actually happening. */
  isLive?: boolean;
  /** Meet link, surfaced as a Join button for the duration of the call. */
  joinHref?: string;
  /** Present on an interview the member is allowed to cancel. */
  bookingId?: string;
}

/**
 * How long an appointment stays on the list once it has started.
 *
 * Used when the booking carries no `endAt` — older bookings, and slots ops
 * created without one.
 */
const DEFAULT_APPOINTMENT_MINUTES = 60;

/**
 * Grace period after the end time before an appointment drops off.
 *
 * Calls run over, and a member refreshing at the scheduled end should not
 * watch their appointment vanish while they are still on it.
 */
const LIVE_GRACE_MINUTES = 15;

export interface ComingUpInput {
  now?: Date;
  scheduledTests: ScheduledTestRecord[];
  scheduledTestsStatus: SourceStatus;
  booking: BookingSummary | null;
  bookingStatus: SourceStatus;
  plan: SurveillancePlanRecord | null;
  planStatus: SourceStatus;
  limit?: number;
}

const DEFAULT_LIMIT = 5;

export function buildComingUp(input: ComingUpInput): ComingUpEvent[] {
  const now = input.now ?? new Date();
  const limit = input.limit ?? DEFAULT_LIMIT;
  const events: ComingUpEvent[] = [];

  if (isUsable(input.scheduledTestsStatus)) {
    for (const test of input.scheduledTests) {
      const at = coerceDate(test.scheduledFor);
      if (!at || at.getTime() < now.getTime()) continue;
      events.push({
        id: `test-${test.id}`,
        at,
        dateLabel: formatDateLabel(at),
        title: test.name,
        detail: test.provider ? `With ${test.provider}` : 'Scheduled test',
        href: '/testing',
        kind: 'test',
      });
    }
  }

  // An interview stays listed until it has actually finished, not until it
  // starts. Dropping it at `startAt` meant a member who opened the dashboard
  // at their appointment time was told "Nothing scheduled" — precisely when
  // they most needed the joining link.
  if (
    isUsable(input.bookingStatus)
    && input.booking?.startAt
    && input.booking.status !== 'cancelled'
  ) {
    const at = input.booking.startAt;
    const endsAt = input.booking.endAt
      ?? new Date(at.getTime() + DEFAULT_APPOINTMENT_MINUTES * 60_000);
    const dropOff = endsAt.getTime() + LIVE_GRACE_MINUTES * 60_000;
    const isLive = now.getTime() >= at.getTime() && now.getTime() < dropOff;

    if (at.getTime() >= now.getTime() || isLive) {
      events.push({
        // Keyed by booking, not a constant. A member can hold more than one
        // interview now that a clinician can arrange a follow-up, and two
        // events sharing an id collide as one row.
        id: input.booking.bookingId
          ? `clinical-history-interview-${input.booking.bookingId}`
          : 'clinical-history-interview',
        at,
        dateLabel: isLive ? 'Now' : formatDateLabel(at),
        title: 'Clinical history interview',
        detail: input.booking.doctorName ? `With ${input.booking.doctorName}` : 'With your clinician',
        kind: 'interview',
        isLive,
        joinHref: input.booking.meetingLink ?? undefined,
        bookingId: input.booking.bookingId ?? undefined,
      });
    }
  }

  if (isUsable(input.planStatus) && input.plan?.items?.length) {
    input.plan.items.forEach((item, index) => {
      const at = coerceDate(item.nextDue);
      if (!at || at.getTime() < now.getTime()) return;
      events.push({
        // Plan items carry no id of their own, so the index disambiguates two
        // items with the same action.
        id: `plan-${index}-${item.action}`,
        at,
        dateLabel: formatDateLabel(at),
        title: `${item.modality}: ${item.action}`,
        detail: item.reason || 'From your monitoring plan',
        href: '/plan',
        kind: 'plan_item',
      });
    });
  }

  return events.sort((left, right) => left.at.getTime() - right.at.getTime()).slice(0, limit);
}
